import { ref } from 'vue'

// True while the walkthrough shows example data. The account query and the router read it.
export const isPracticeActive = ref(false)

const PRACTICE_ID_PREFIX = 'practice-'

// The Vue Query key scope for the example account entry. The real entry stays under its own key.
export const PRACTICE_QUERY_SCOPE = 'onboarding-practice'

// Object keys that carry an id: `ticket_id`, `ticketId`, `approval_ids`, `companyId`, and so on.
const ID_KEY_PATTERN = /(_id|_ids|Id)$/

// Query keys that can hold an example id: a request or an approval.
export const PRACTICE_ROUTE_QUERY_KEYS = ['ticketId', 'approvalId']

// Maps whose keys are question ids. Their values are answers, which are free text and never checked.
const ID_MAP_KEYS = ['answers', 'questions_answers']

export function isPracticeId(value) {
  return typeof value === 'string' && value.startsWith(PRACTICE_ID_PREFIX)
}

/**
 * True when any part carries an example id. A string part is a request URL, and only its path
 * segments and query values are read. An object or FormData part is read only in its id fields,
 * never its free text, so a search for "practice-" or a message that starts with it passes.
 */
export function hasPracticeId(...parts) {
  return parts.some((part) =>
    typeof part === 'string' ? urlHasPracticeId(part) : containsPracticeId(part)
  )
}

// True when the route's id (the details or wizard id, or a query id) is an example id.
export function hasPracticeRouteId(route) {
  return (
    isPracticeId(route.params.ticketId) ||
    PRACTICE_ROUTE_QUERY_KEYS.some((key) => isPracticeId(route.query[key]))
  )
}

function urlHasPracticeId(url) {
  return url.split(/[/?&=#]/).some(isPracticeId)
}

function isStructure(value) {
  return value !== null && typeof value === 'object'
}

function containsPracticeId(value) {
  if (!isStructure(value)) return false

  if (typeof FormData !== 'undefined' && value instanceof FormData) {
    return [...value.entries()].some(
      ([key, entry]) => ID_KEY_PATTERN.test(key) && isPracticeId(entry)
    )
  }

  return Object.entries(value).some(([key, entry]) => {
    if (ID_MAP_KEYS.includes(key) && isStructure(entry) && !Array.isArray(entry)) {
      return Object.keys(entry).some(isPracticeId)
    }
    if (ID_KEY_PATTERN.test(key)) return holdsPracticeId(entry)
    return containsPracticeId(entry)
  })
}

// The value under an id key: a string, an array of ids, or an object that holds ids.
function holdsPracticeId(entry) {
  if (Array.isArray(entry)) return entry.some(holdsPracticeId)
  if (isStructure(entry)) return containsPracticeId(entry)
  return isPracticeId(entry)
}
